import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

class CharmPicker extends HTMLElement {
  constructor() {
    super();
    this.openButton = null;
    this.closeButton = null;
    this.overlay = null;
    this.modal = null;
  }

  connectedCallback() {
    this.openButton = this.querySelector('[data-ref="charm-picker-open"]');
    this.closeButton = this.querySelector('[data-ref="charm-picker-close"]');
    this.overlay = this.querySelector('[data-ref="charm-picker-overlay"]');
    this.modal = this.querySelector('[data-ref="charm-picker-modal"]');

    document.body.appendChild(this.overlay);
    document.body.appendChild(this.modal);

    this.openButton?.addEventListener('click', this.open);
    this.closeButton?.addEventListener('click', this.close);
    this.overlay?.addEventListener('click', this.close);

    this.modal.querySelectorAll('[data-ref="charm-card"]').forEach((card) => {
      this.setupCard(card);
    });
  }

  setupCard(card) {
    const ctaBtn = card.querySelector('[data-ref="charm-card-cta"]');
    const ctaIcon = card.querySelector('[data-ref="charm-card-cta-icon"]');
    const ctaSpinner = card.querySelector('[data-ref="charm-card-cta-spinner"]');
    const variantBtns = card.querySelectorAll('[data-ref="charm-variant-btn"]');
    const priceEl = card.querySelector('[data-ref="charm-card-price"]');
    const slides = Array.from(card.querySelectorAll('[data-ref="charm-carousel-slide"]'));
    const prevBtn = card.querySelector('[data-ref="charm-carousel-prev"]');
    const nextBtn = card.querySelector('[data-ref="charm-carousel-next"]');

    const showSlideByIndex = (index) => {
      if (slides.length === 0) return;
      const wrapped = ((index % slides.length) + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('charm-picker__carousel-slide--active', i === wrapped);
      });
      card.dataset.currentSlide = wrapped;
    };

    const showSlideByImageId = (imageId) => {
      if (!imageId) return;
      const index = slides.findIndex((slide) => slide.dataset.imageId === imageId);
      if (index !== -1) showSlideByIndex(index);
    };

    prevBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const current = parseInt(card.dataset.currentSlide || '0');
      showSlideByIndex(current - 1);
    });

    nextBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const current = parseInt(card.dataset.currentSlide || '0');
      showSlideByIndex(current + 1);
    });

    variantBtns.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        variantBtns.forEach((b) => b.classList.remove('charm-picker__variant-btn--active'));
        btn.classList.add('charm-picker__variant-btn--active');

        ctaBtn.dataset.variantId = btn.dataset.variantId;
        if (priceEl && btn.dataset.price) {
          priceEl.textContent = this.formatMoney(btn.dataset.price);
        }

        showSlideByImageId(btn.dataset.imageId);
      });
    });

    ctaBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.addToCart(ctaBtn.dataset.variantId, ctaBtn, ctaIcon, ctaSpinner);
    });
  }

  formatMoney(cents) {
    return (parseInt(cents) / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    });
  }

  open = () => {
    this.overlay.hidden = false;
    this.modal.hidden = false;
  };

  close = () => {
    this.overlay.hidden = true;
    this.modal.hidden = true;
  };

  getCartAddUrl() {
    if (window.Theme?.routes?.cart_add_url) {
      return window.Theme.routes.cart_add_url;
    }
    return '/cart/add.js';
  }

  openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    document.dispatchEvent(new Event(ThemeEvents.cartOpen, { bubbles: true }));
  }

  setLoadingState(ctaBtn, ctaIcon, ctaSpinner, isLoading) {
    ctaBtn.disabled = isLoading;
    // On force les styles en ligne directement (au lieu de compter sur une classe CSS)
    // pour éviter tout conflit avec d'autres styles du thème qui pourraient l'emporter.
    if (ctaIcon) ctaIcon.style.setProperty('display', isLoading ? 'none' : 'flex', 'important');
    if (ctaSpinner) ctaSpinner.style.setProperty('display', isLoading ? 'block' : 'none', 'important');
  }

  addToCart = async (charmVariantId, ctaBtn, ctaIcon, ctaSpinner) => {
    if (!charmVariantId) return;

    this.setLoadingState(ctaBtn, ctaIcon, ctaSpinner, true);

    const sections = [];
    if (document.querySelector('cart-drawer')) {
      sections.push(document.querySelector('cart-drawer').dataset.sectionId);
    }

    try {
      const response = await fetch(this.getCartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: charmVariantId, quantity: 1 }],
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            source: 'charm-picker',
            sections: data.sections,
          },
        ),
      );

      this.close();
      this.openCartDrawer();
    } catch (error) {
      console.error('[charm-picker] error al añadir al carrito', error);
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: { type: 'error', message: error },
        }),
      );
    } finally {
      this.setLoadingState(ctaBtn, ctaIcon, ctaSpinner, false);
    }
  };
}

if (!customElements.get('charm-picker')) {
  customElements.define('charm-picker', CharmPicker);
}
